import { useMemo } from 'react'
import { type ImportedGoodExtended } from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useCalculateBatchLiability } from '@/features/cbam-reporting/hooks/cbamLiability'
import { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { getDefaultCbamModifiers } from '@/shared/types/cbamModifiers'

function getSpecificDirectEmissionsFromSupplierEmissionsReport(
  good: ImportedGood,
  supplierEmissionsReports: SupplierEmissionsReport[]
) {
  if (good.isUsingDefaultEmissions) {
    return null
  }

  return (
    supplierEmissionsReports.find(report => report.id === good.supplierEmissionsReportId)
      ?.specificDirectEmbeddedEmissions ?? 0
  )
}

function getSpecificIndirectEmissionsFromSupplierEmissionsReport(
  good: ImportedGood,
  supplierEmissionsReports: SupplierEmissionsReport[]
) {
  if (good.isUsingDefaultEmissions) {
    return null
  }

  return (
    supplierEmissionsReports.find(report => report.id === good.supplierEmissionsReportId)
      ?.specificIndirectEmbeddedEmissions ?? 0
  )
}

/** Same batch request shape as `useSimulationWithLiabilty` (100% CSCF base + optional user CSCF high run). */
function mapGoodToEstimatorBatchRequest(
  good: ImportedGood,
  supplierEmissionsReports: SupplierEmissionsReport[],
  cscfPercent: number
) {
  return {
    id: good.id,
    code: good.cnCode,
    country: good.country,
    volume: good.netMassInTonnes,
    isActualValues: !good.isUsingDefaultEmissions,
    importDate: good.importDate,
    ...(good.quarter != null && { quarter: good.quarter }),
    directEmissionsPerTonne: good.isUsingDefaultEmissions
      ? null
      : getSpecificDirectEmissionsFromSupplierEmissionsReport(good, supplierEmissionsReports),
    indirectEmissionsPerTonne: good.isUsingDefaultEmissions
      ? null
      : getSpecificIndirectEmissionsFromSupplierEmissionsReport(good, supplierEmissionsReports),
    cscfPercent
  }
}

type UseImportedGoodsWithLiabilityOptions = {
  /** Included in batch liability query keys; defaults to empty string. */
  productionMethodCode?: string
  /**
   * When true (estimator-era reporting), batch liability matches Impact Estimator:
   * base run at 100% CSCF and optional second run at user CSCF when &lt; 100%.
   */
  useEstimatorBatchLiability?: boolean
  modifiers?: CbamModifiers
}

/**
 * Batch liability for an arbitrary list of imported goods (typically the filter-selected subset).
 * Does not load data; pass rows from `useCbamReportImportedGoods` and the same
 * `supplierEmissionsReports` used to enrich them.
 */
export const useImportedGoodsWithLiability = (
  goodsForLiability: ImportedGoodExtended[],
  supplierEmissionsReports: SupplierEmissionsReport[],
  options?: UseImportedGoodsWithLiabilityOptions
) => {
  const productionMethodCode = options?.productionMethodCode ?? ''
  const useEstimatorBatch = Boolean(options?.useEstimatorBatchLiability)
  const modifiers = options?.modifiers ?? getDefaultCbamModifiers()
  const userCscfPercent = parseFloat(modifiers.cscfPercentage) || 100
  const applyHighCscf =
    useEstimatorBatch && !Number.isNaN(userCscfPercent) && userCscfPercent !== 100

  const legacyBatchRequests = useMemo(
    () =>
      goodsForLiability.map(good => ({
        id: good.id,
        code: good.cnCode,
        country: good.country,
        volume: good.netMassInTonnes,
        isActualValues: !good.isUsingDefaultEmissions,
        importDate: good.importDate,
        directEmissionsPerTonne: getSpecificDirectEmissionsFromSupplierEmissionsReport(
          good,
          supplierEmissionsReports
        ),
        indirectEmissionsPerTonne: getSpecificIndirectEmissionsFromSupplierEmissionsReport(
          good,
          supplierEmissionsReports
        )
      })),
    [goodsForLiability, supplierEmissionsReports]
  )

  const estimatorBaseRequests = useMemo(() => {
    if (!useEstimatorBatch) {
      return []
    }
    return goodsForLiability.map(good =>
      mapGoodToEstimatorBatchRequest(good, supplierEmissionsReports, 100)
    )
  }, [useEstimatorBatch, goodsForLiability, supplierEmissionsReports])

  const estimatorHighRequests = useMemo(() => {
    if (!useEstimatorBatch || !applyHighCscf) {
      return []
    }
    return goodsForLiability.map(good =>
      mapGoodToEstimatorBatchRequest(good, supplierEmissionsReports, userCscfPercent)
    )
  }, [
    useEstimatorBatch,
    applyHighCscf,
    goodsForLiability,
    supplierEmissionsReports,
    userCscfPercent
  ])

  const baseQueryKey = JSON.stringify({
    productionMethodCode,
    cscf: useEstimatorBatch ? 100 : undefined
  })
  const highQueryKey = JSON.stringify({
    productionMethodCode,
    cscf: userCscfPercent
  })

  const batchRequests = useEstimatorBatch ? estimatorBaseRequests : legacyBatchRequests

  const { data: liabilityDataBaseLow, isLoading: isBaseLoading } = useCalculateBatchLiability(
    { requests: batchRequests },
    baseQueryKey
  )

  const { data: liabilityDataHigh, isLoading: isHighLoading } = useCalculateBatchLiability(
    { requests: useEstimatorBatch ? estimatorHighRequests : [] },
    highQueryKey
  )

  const liabilityDataHighForScenario =
    useEstimatorBatch && applyHighCscf ? liabilityDataHigh : undefined
  const liabilityData = liabilityDataBaseLow

  const isBatchLiabilityLoading =
    isBaseLoading || (useEstimatorBatch && applyHighCscf && isHighLoading)

  return {
    liabilityData,
    liabilityDataBaseLow,
    liabilityDataHighForScenario,
    isBatchLiabilityLoading
  }
}
